import { useState } from 'react';
import styled from 'styled-components';
import {
  Loading,
  Flex,
  FlexColumn,
  flexColumn,
  Breadcrumbs,
  LoadingButton,
  Link,
} from '@elementary/common-ui/components';
import { Typography, InputLabel, Tooltip } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useAuth } from '@frontegg/react';
import useFileDownload from 'shared/hooks/useFileDownload';
import useAccount from '@elementary/common-ui/hooks/useAccount';
import useSupportChat from 'shared/hooks/useSupportChat';
import { TextField } from '@elementary/common-ui/components';
import useAccountPermission from '@elementary/common-ui/hooks/useAccountPermission';
import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';
import {
  AccountPermission,
  EnvironmentPermission,
} from '@elementary/common-ui/types';
import { fetchActivityLogsExport } from './api';
import ExternalIntegrationsSection from './ExternalIntegrations/ExternalIntegrationsSection';

const Wrapper = styled(FlexColumn)`
  width: 100%;
  gap: 24px;
`;

const Form = styled.form`
  ${flexColumn};
  gap: 16px;
  width: 100%;
`;

const Frame = styled.div`
  border: 1px solid ${({ theme }) => theme.palette.common.gray13};
  border-radius: 6px;
  padding: 32px;
`;

const FieldsContainer = styled(FlexColumn)`
  gap: 38px;
`;

type ActivityLogsForm = {
  days_back: number;
};

type ActivityLogSectionProps = {
  title: string;
  logType: 'audit' | 'system';
  filenamePrefix: string;
  account?: { activity_logs_enabled?: boolean };
  supportChat: { show: () => void };
  accessToken?: string;
  accountPermission?: AccountPermission;
  envPermission?: EnvironmentPermission;
};

const ActivityLogSection = ({
  title,
  logType,
  filenamePrefix,
  account,
  supportChat,
  accessToken,
  accountPermission,
  envPermission,
}: ActivityLogSectionProps) => {
  const [isExporting, setIsExporting] = useState(false);
  const hasAccountPermission = useAccountPermission();
  const hasEnvPermission = useEnvPermission();

  const hasRequiredPermission = accountPermission
    ? hasAccountPermission(accountPermission)
    : envPermission
    ? hasEnvPermission(envPermission)
    : true;
  const { handleSubmit, control, watch } = useForm<ActivityLogsForm>({
    defaultValues: {
      days_back: 180,
    },
  });
  const { downloadFile: downloadActivityLog } = useFileDownload({
    successMessage: 'Activity logs exported successfully',
    errorMessage: 'Failed to export activity logs',
  });

  const onExport = async (data: ActivityLogsForm) => {
    setIsExporting(true);
    try {
      const response = await fetchActivityLogsExport(
        data.days_back,
        logType,
        accessToken,
      );
      const filename = `${filenamePrefix}-activity-logs-${
        new Date().toISOString().split('T')[0]
      }.csv`;
      await downloadActivityLog(response, filename);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Frame>
      <Flex
        sx={{
          justifyContent: 'space-between',
        }}
      >
        <FlexColumn>
          <Typography sx={{ fontWeight: 'bold' }}>{title}</Typography>
        </FlexColumn>
        <Tooltip
          title={
            !account?.activity_logs_enabled ? (
              <span>
                Activity logs are available on higher-tier plans.{' '}
                <Link onClick={supportChat.show} href="#">
                  Contact us
                </Link>{' '}
                to learn more.
              </span>
            ) : !hasRequiredPermission ? (
              <span>
                You do not have permission to export {title.toLowerCase()}.
              </span>
            ) : undefined
          }
          placement="top-start"
        >
          <div>
            <LoadingButton
              variant="outlined"
              onClick={handleSubmit(onExport)}
              disabled={
                !account?.activity_logs_enabled ||
                !hasRequiredPermission ||
                !watch('days_back') ||
                watch('days_back') <= 0
              }
              loading={isExporting}
            >
              Export to CSV
            </LoadingButton>
          </div>
        </Tooltip>
      </Flex>
      <Form>
        <FieldsContainer>
          <FlexColumn>
            <InputLabel shrink>Export period (days)</InputLabel>
            <Controller
              render={({ field }) => (
                <TextField
                  {...field}
                  type="number"
                  placeholder="Enter number of days"
                  inputProps={{ min: 1, max: 365 }}
                  sx={{ width: 200 }}
                />
              )}
              name="days_back"
              control={control}
            />
          </FlexColumn>
        </FieldsContainer>
      </Form>
    </Frame>
  );
};

const Logs = () => {
  const { user } = useAuth();
  const supportChat = useSupportChat();
  const { data: account, isLoading: isAccountLoading } = useAccount();

  return isAccountLoading ? (
    <Loading />
  ) : (
    <Wrapper>
      <Breadcrumbs items={['Account', 'Logs']} showTreeButton={false} />
      <ActivityLogSection
        title="User Activity Logs"
        logType="audit"
        filenamePrefix="user"
        account={account}
        supportChat={supportChat}
        accessToken={user?.accessToken}
        accountPermission={'VIEW_AUDIT_LOGS'}
      />
      <ActivityLogSection
        title="System Logs"
        logType="system"
        filenamePrefix="system"
        account={account}
        supportChat={supportChat}
        accessToken={user?.accessToken}
        accountPermission={'VIEW_SYSTEM_LOGS'}
      />
      <ExternalIntegrationsSection
        account={account}
        supportChat={supportChat}
      />
    </Wrapper>
  );
};

export default Logs;
